import { ArrowRight, FileArchive, UploadCloud, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const MAX_BYTES = 5 * 1024 * 1024;

function validate(file: File): string | null {
  const lower = file.name.toLowerCase();
  if (!lower.endsWith(".zip") && !lower.endsWith(".skill"))
    return "Only .zip or .skill files are allowed";
  if (file.size > MAX_BYTES) return "File exceeds the 5 MB limit";
  return null;
}

export function UploadStep({
  file,
  onFile,
  onContinue,
}: {
  file: File | null;
  onFile: (f: File | null) => void;
  onContinue: () => void;
}) {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function accept(f: File | undefined) {
    if (!f) return;
    const err = validate(f);
    if (err) {
      toast.error(err);
      return;
    }
    onFile(f);
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-sm font-semibold text-fg-1">
          Skill package <span className="text-danger">*</span>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            accept(e.dataTransfer.files[0]);
          }}
          className={cn(
            "mt-2 flex w-full flex-col items-center gap-2 rounded-[12px] border-2 border-dashed px-6 py-12 text-center transition-colors",
            over ? "border-fg-link bg-bg-2" : "border-border-2 bg-white hover:bg-bg-2",
          )}
        >
          <UploadCloud className="size-8 text-fg-link" />
          <span className="font-display text-lg font-bold text-fg-1">
            Drop your skill package here
          </span>
          <span className="text-sm text-fg-3">or click to browse · .zip or .skill, max 5 MB</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".zip,.skill"
          className="hidden"
          onChange={(e) => accept(e.target.files?.[0])}
        />
      </div>

      {file ? (
        <div className="flex items-center gap-3 rounded-[9px] border border-border-1 bg-white p-3">
          <span className="grid size-9 place-items-center rounded-md bg-bg-2 text-fg-link">
            <FileArchive className="size-4" />
          </span>
          <div className="flex-1">
            <div className="text-sm font-medium text-fg-1">{file.name}</div>
            <div className="text-xs text-fg-3">{(file.size / 1024).toFixed(1)} KB</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onFile(null)} aria-label="Remove file">
            <X className="size-4" />
          </Button>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button disabled={!file} onClick={onContinue}>
          Continue
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}
